import React from 'react';
import { Pagination } from '@shared';
import type { PaginationLabels } from '@shared';
import { ADMIN_PAGINATION_LABELS } from '@/lib/paginationLabels';

/**
 * Admin paginator for backend-paged list pages — a thin offset/limit adapter
 * over the shared `Pagination` standard bar (the same component the tenant
 * uses). The parent owns ``offset`` / ``limit`` / ``total`` and re-fetches on
 * page change; this maps that to the shared bar's page model.
 *
 * Admin is Spanish-only (no i18n) → labels are hardcoded Spanish here. Pass
 * ``onLimitChange`` to expose the 20/50/200 page-size selector (the parent must
 * refetch from offset 0 with the new limit); omit it for a fixed page size.
 */

interface PaginatorProps {
    /** Zero-based row index of the first row currently shown. */
    offset: number;
    /** Page size — the number of rows the parent requested. */
    limit: number;
    /** Total rows that match the current filters. */
    total: number;
    /** Called with the new ``offset`` when the user changes page. */
    onChange: (nextOffset: number) => void;
    /** Optional — expose the page-size selector (parent refetches from page 1). */
    onLimitChange?: (limit: number) => void;
    /** Optional disabled override (e.g. while a request is in flight). */
    disabled?: boolean;
    className?: string;
}

const LABELS: PaginationLabels = ADMIN_PAGINATION_LABELS;

const Paginator: React.FC<PaginatorProps> = ({ offset, limit, total, onChange, onLimitChange, disabled, className }) => {
    const page = Math.floor(offset / Math.max(1, limit)) + 1;
    return (
        <Pagination
            page={page}
            pageSize={limit}
            totalItems={total}
            onPageChange={p => onChange(Math.max(0, (p - 1) * limit))}
            onPageSizeChange={onLimitChange}
            labels={LABELS}
            disabled={disabled}
            className={className}
        />
    );
};

export default Paginator;
